import { CheckCircle2, CircleAlert, Link2 } from 'lucide-react';
import type { PaigeDestination } from './paige.global';

export interface StorageConnectionProps {
  /** The stored destination; absent while it is being added. */
  current?: PaigeDestination;
  /** The product's name ("Microsoft 365"). */
  product: string;
  busy: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

/**
 * The account line of a storage destination's sheet: who it is connected as, "connect again" when
 * the provider stopped letting Paige in, or not connected yet; with Connect, Reconnect or Disconnect.
 * A destination being added connects from the sheet's own "Add and connect".
 */
export function StorageConnection({
  current,
  product,
  busy,
  onConnect,
  onDisconnect,
}: StorageConnectionProps) {
  const connection = current?.settings.connection;
  if (!current) {
    return (
      <p className="paige-storage-connection">
        <Link2 size={16} aria-hidden="true" />
        <span>
          Add it, then sign in to {product} in the window that opens. Paige acts as the account you
          sign in with.
        </span>
      </p>
    );
  }
  const connected = connection?.state === 'connected';
  return (
    <p className={`paige-storage-connection${connection?.state === 'reconnect' ? ' is-down' : ''}`}>
      {connected ? (
        <CheckCircle2 size={16} aria-hidden="true" />
      ) : (
        <CircleAlert size={16} aria-hidden="true" />
      )}
      <span>{connectionLine(product, connection)}</span>
      <button type="button" className="quiet-button small" disabled={busy} onClick={onConnect}>
        {connection ? 'Reconnect' : 'Connect'}
      </button>
      {connection && (
        <button type="button" className="quiet-button small" disabled={busy} onClick={onDisconnect}>
          Disconnect
        </button>
      )}
    </p>
  );
}

function connectionLine(
  product: string,
  connection: PaigeDestination['settings']['connection'],
): string {
  if (!connection) return `Not connected to ${product} yet`;
  if (connection.state === 'reconnect')
    return `${product} no longer lets Paige in as ${connection.account ?? 'that account'}; connect again`;
  return `Connected as ${connection.account ?? `a ${product} account`}`;
}
